import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService } from 'primeng/api';

import {
  CompanyFilters,
  ProspectCompany,
  ProspectContact,
  ProspectContactMini,
  ProspectionService,
  Referentiels,
} from '../../services/prospection.service';

interface Option {
  label: string;
  value: any;
}

@Component({
  selector: 'app-prospect-companies',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    DropdownModule,
    InputTextModule,
    ProgressSpinnerModule,
    Textarea,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './prospect-companies.component.html',
  styleUrls: ['./prospect-companies.component.scss'],
})
export class ProspectCompaniesComponent implements OnInit {
  companies: ProspectCompany[] = [];
  loading = false;

  first = 0;
  pageSize = 25;
  totalCount = 0;

  search = '';
  filters: CompanyFilters = { tri: 'score' };
  private readonly filtersKey = 'prospectionFiltersState';

  referentiels?: Referentiels;
  optionsStatut: Option[] = [];
  optionsProduit: Option[] = [];
  optionsSecteur: Option[] = [];
  optionsTri: Option[] = [
    { label: 'Score décroissant', value: 'score' },
    { label: 'Signal le plus récent', value: 'trigger' },
    { label: 'Ajout le plus récent', value: 'recent' },
    { label: 'Raison sociale', value: 'raison_sociale' },
  ];
  optionsScoreMin: Option[] = [
    { label: 'Tous les scores', value: null },
    { label: 'Score ≥ 40', value: 40 },
    { label: 'Score ≥ 60 (travaillables)', value: 60 },
    { label: 'Score ≥ 80 (prioritaires)', value: 80 },
  ];
  optionsTrigger: Option[] = [
    { label: 'Avec ou sans signal', value: null },
    { label: 'Avec signal d\'achat', value: true },
    { label: 'Sans signal', value: false },
  ];

  // --- Fiche détaillée ---
  detailVisible = false;
  companySelectionnee?: ProspectCompany;
  contactsDuProspect: ProspectContact[] = [];
  chargementDetail = false;

  // --- Ajout de contact ---
  contactVisible = false;
  nouveauContact: Partial<ProspectContact> = {};
  optionsPersona: Option[] = [
    { label: 'Dirigeant', value: 'DIRIGEANT' },
    { label: 'DAF', value: 'DAF' },
    { label: 'RH', value: 'RH' },
    { label: 'Office Manager', value: 'OFFICE_MANAGER' },
    { label: 'Autre', value: 'AUTRE' },
  ];

  // --- Consentement ---
  consentementVisible = false;
  contactConsentement?: ProspectContact;
  consentementSource: string | null = null;
  consentementPreuve = '';
  optionsConsentement: Option[] = [
    { label: 'Réponse LinkedIn', value: 'LINKEDIN_REPLY' },
    { label: 'Réponse email', value: 'EMAIL_REPLY' },
    { label: 'Formulaire rempli', value: 'FORMULAIRE' },
    { label: 'Appel entrant', value: 'APPEL_ENTRANT' },
    { label: 'Transmis par un apporteur', value: 'APPORTEUR' },
  ];

  // --- Import CSV ---
  importVisible = false;
  fichierImport?: File;
  campagneImport = '';
  importEnCours = false;

  constructor(
    private prospectionService: ProspectionService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.restaurerFiltres();
    this.chargerReferentiels();
    // Le chargement des données est déclenché par le (onLazyLoad) du tableau.
  }

  private chargerReferentiels(): void {
    this.prospectionService.getReferentiels().subscribe({
      next: (ref) => {
        this.referentiels = ref;
        this.optionsStatut = [
          { label: 'Tous les statuts', value: null },
          ...ref.statuts.map((s) => ({ label: this.libelleStatut(s), value: s })),
        ];
        this.optionsProduit = [
          { label: 'Tous les produits', value: null },
          ...ref.produits.map((p) => ({ label: this.libelleProduit(p), value: p })),
        ];
        this.optionsSecteur = [
          { label: 'Tous les secteurs', value: null },
          ...ref.secteurs
            .filter((s) => s.priorite !== 'APPORTEUR')
            .map((s) => ({ label: s.libelle, value: s.cle })),
        ];
      },
      error: () => {
        this.messageService.add({
          severity: 'warn',
          summary: 'Référentiels',
          detail: 'Les listes de filtres n\'ont pas pu être chargées.',
        });
      },
    });
  }

  // ------------------------------------------------------------------ Chargement

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    this.pageSize = event.rows ?? this.pageSize;
    this.charger();
  }

  charger(): void {
    this.loading = true;
    this.persisterFiltres();
    const recherche = this.search?.trim() || undefined;

    this.prospectionService.countCompanies(recherche, this.filters).subscribe({
      next: (resultat) => {
        this.totalCount = resultat.count;
      },
      error: () => {
        this.totalCount = 0;
      },
    });

    this.prospectionService
      .listCompanies(this.pageSize, this.first, recherche, this.filters)
      .subscribe({
        next: (companies) => {
          this.companies = companies;
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Chargement',
            detail: err?.error?.detail || 'Impossible de charger les prospects.',
          });
        },
      });
  }

  appliquerFiltres(): void {
    this.first = 0;
    this.charger();
  }

  reinitialiserFiltres(): void {
    this.search = '';
    this.filters = { tri: 'score' };
    this.first = 0;
    this.charger();
  }

  private restaurerFiltres(): void {
    try {
      const brut = sessionStorage.getItem(this.filtersKey);
      if (!brut) {
        return;
      }
      const etat = JSON.parse(brut);
      this.search = etat.search || '';
      this.filters = etat.filters || { tri: 'score' };
    } catch {
      // Un état corrompu ne doit pas empêcher l'ouverture de la page.
    }
  }

  private persisterFiltres(): void {
    try {
      sessionStorage.setItem(
        this.filtersKey,
        JSON.stringify({ search: this.search, filters: this.filters })
      );
    } catch {
      // sessionStorage indisponible (navigation privée) : sans conséquence.
    }
  }

  // ------------------------------------------------------------------ Détail

  ouvrirDetail(company: ProspectCompany): void {
    this.companySelectionnee = company;
    this.detailVisible = true;
    this.chargementDetail = true;
    this.contactsDuProspect = [];

    this.prospectionService.listContacts({ company_id: company.id, limit: 50 }).subscribe({
      next: (contacts) => {
        this.contactsDuProspect = contacts;
        this.chargementDetail = false;
      },
      error: () => {
        this.chargementDetail = false;
      },
    });
  }

  rescorer(company: ProspectCompany): void {
    this.prospectionService.rescoreCompany(company.id).subscribe({
      next: (maj) => {
        Object.assign(company, maj);
        this.messageService.add({
          severity: 'success',
          summary: 'Score recalculé',
          detail: `${maj.raison_sociale} : ${maj.score}/100`,
        });
      },
      error: (err) =>
        this.messageService.add({
          severity: 'error',
          summary: 'Rescoring',
          detail: err?.error?.detail || 'Échec du recalcul.',
        }),
    });
  }

  // ------------------------------------------------------------------ Contacts

  ouvrirAjoutContact(company: ProspectCompany): void {
    this.companySelectionnee = company;
    this.nouveauContact = {
      company_id: company.id,
      prenom: company.dirigeant_prenom || '',
      nom: company.dirigeant_nom || '',
      fonction: company.dirigeant_qualite || '',
      persona: 'DIRIGEANT',
    };
    this.contactVisible = true;
  }

  enregistrerContact(): void {
    if (!this.nouveauContact.company_id) {
      return;
    }
    this.prospectionService.createContact(this.nouveauContact).subscribe({
      next: (contact) => {
        this.contactVisible = false;
        this.contactsDuProspect = [...this.contactsDuProspect, contact];
        this.messageService.add({
          severity: 'success',
          summary: 'Contact ajouté',
          detail: `${contact.prenom || ''} ${contact.nom || ''}`.trim(),
        });
        this.charger();
      },
      error: (err) =>
        this.messageService.add({
          severity: 'error',
          summary: 'Ajout du contact',
          detail: err?.error?.detail || 'Échec de la création.',
        }),
    });
  }

  // ------------------------------------------------------------------ Conformité

  ouvrirConsentement(contact: ProspectContact): void {
    this.contactConsentement = contact;
    this.consentementSource = null;
    this.consentementPreuve = '';
    this.consentementVisible = true;
  }

  enregistrerConsentement(): void {
    if (!this.contactConsentement || !this.consentementSource) {
      return;
    }
    if (this.consentementPreuve.trim().length < 3) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Preuve requise',
        detail: 'Collez le verbatim de la réponse reçue : c\'est la pièce qui justifie l\'appel.',
      });
      return;
    }

    this.prospectionService
      .enregistrerConsentement(
        this.contactConsentement.id,
        this.consentementSource,
        this.consentementPreuve.trim()
      )
      .subscribe({
        next: (contact) => {
          this.consentementVisible = false;
          this.remplacerContact(contact);
          this.messageService.add({
            severity: 'success',
            summary: 'Consentement enregistré',
            detail: 'Le canal téléphonique est débloqué pour ce contact.',
          });
          this.charger();
        },
        error: (err) =>
          this.messageService.add({
            severity: 'error',
            summary: 'Consentement',
            detail: err?.error?.detail || 'Échec de l\'enregistrement.',
          }),
      });
  }

  confirmerOpposition(contact: ProspectContact): void {
    this.confirmationService.confirm({
      header: 'Droit d\'opposition RGPD',
      message:
        `Enregistrer l'opposition de ${contact.prenom || ''} ${contact.nom || ''} ? ` +
        'Tous les canaux seront définitivement coupés pour ce contact et il devra être ' +
        'retiré des outils d\'envoi. Cette action n\'est pas réversible depuis cet écran.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Enregistrer l\'opposition',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.prospectionService.enregistrerOpposition(contact.id).subscribe({
          next: (maj) => {
            this.remplacerContact(maj);
            this.messageService.add({
              severity: 'success',
              summary: 'Opposition enregistrée',
              detail: 'Le contact est exclu de tous les canaux.',
            });
            this.charger();
          },
          error: (err) =>
            this.messageService.add({
              severity: 'error',
              summary: 'Opposition',
              detail: err?.error?.detail || 'Échec de l\'enregistrement.',
            }),
        });
      },
    });
  }

  private remplacerContact(contact: ProspectContact): void {
    this.contactsDuProspect = this.contactsDuProspect.map((c) =>
      c.id === contact.id ? contact : c
    );
  }

  // ------------------------------------------------------------------ Actions séquence

  journaliser(contact: ProspectContact, canal: string, action: string): void {
    this.prospectionService
      .logActivity({ contact_id: contact.id, canal, action })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Action journalisée',
            detail: this.libelleAction(action),
          });
          this.charger();
        },
        error: (err) =>
          this.messageService.add({
            severity: 'error',
            summary: 'Journalisation refusée',
            detail: err?.error?.detail || 'Action impossible.',
            life: 8000,
          }),
      });
  }

  convertir(company: ProspectCompany): void {
    const contactAvecConsentement = this.contactsDuProspect.find((c) => c.appel_autorise);
    this.confirmationService.confirm({
      header: 'Convertir en contact CRM',
      message: `Créer la fiche contact de ${company.raison_sociale} dans le CRM ? Le prospect passera au statut « Converti ».`,
      icon: 'pi pi-user-plus',
      acceptLabel: 'Convertir',
      rejectLabel: 'Annuler',
      accept: () => {
        this.prospectionService
          .convertCompany(company.id, {
            contact_prospect_id: contactAvecConsentement?.id,
          })
          .subscribe({
            next: (resultat) => {
              this.messageService.add({
                severity: 'success',
                summary: resultat.deja_converti ? 'Déjà converti' : 'Converti',
                detail: `Fiche CRM ${resultat.reference || resultat.contact_id}`,
              });
              this.detailVisible = false;
              this.charger();
            },
            error: (err) =>
              this.messageService.add({
                severity: 'error',
                summary: 'Conversion',
                detail: err?.error?.detail || 'Échec de la conversion.',
              }),
          });
      },
    });
  }

  // ------------------------------------------------------------------ Import CSV

  onFichierSelectionne(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.fichierImport = input.files?.[0];
  }

  lancerImport(): void {
    if (!this.fichierImport) {
      return;
    }
    this.importEnCours = true;
    this.prospectionService
      .importerCsv(this.fichierImport, this.campagneImport || undefined)
      .subscribe({
        next: (resultat) => {
          this.importEnCours = false;
          this.importVisible = false;
          this.fichierImport = undefined;
          this.messageService.add({
            severity: 'success',
            summary: 'Import terminé',
            detail: `${resultat.crees} créés, ${resultat.mis_a_jour} mis à jour, ${resultat.ignores} ignorés sur ${resultat.lignes_lues} lignes.`,
            life: 8000,
          });
          this.charger();
        },
        error: (err) => {
          this.importEnCours = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Import',
            detail: err?.error?.detail || 'Échec de l\'import.',
          });
        },
      });
  }

  // ------------------------------------------------------------------ Affichage

  severiteScore(score: number): 'success' | 'warn' | 'danger' | 'info' {
    if (score >= 80) {
      return 'success';
    }
    if (score >= 60) {
      return 'info';
    }
    return score >= 40 ? 'warn' : 'danger';
  }

  severiteStatut(statut: string): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
    switch (statut) {
      case 'CONVERTI':
        return 'success';
      case 'RDV':
      case 'REPONDU':
        return 'info';
      case 'EN_SEQUENCE':
        return 'warn';
      case 'EXCLU':
      case 'NON_INTERESSE':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  libelleStatut(statut: string): string {
    const libelles: { [cle: string]: string } = {
      A_QUALIFIER: 'À qualifier',
      A_CONTACTER: 'À contacter',
      EN_SEQUENCE: 'En séquence',
      REPONDU: 'A répondu',
      RDV: 'Rendez-vous',
      CONVERTI: 'Converti',
      NON_INTERESSE: 'Non intéressé',
      EXCLU: 'Exclu',
    };
    return libelles[statut] || statut;
  }

  libelleProduit(produit?: string): string {
    const libelles: { [cle: string]: string } = {
      SANTE_COLLECTIVE: 'Santé collective',
      PREVOYANCE_COLLECTIVE: 'Prévoyance collective',
      SANTE_PRO_TNS: 'Santé / prévoyance TNS',
      EMPRUNTEUR: 'Assurance emprunteur',
    };
    return produit ? libelles[produit] || produit : '—';
  }

  libelleAction(action: string): string {
    const libelles: { [cle: string]: string } = {
      LINKEDIN_INVITATION: 'Invitation LinkedIn envoyée',
      LINKEDIN_MESSAGE: 'Message LinkedIn envoyé',
      EMAIL_ENVOYE: 'Email envoyé',
      APPEL: 'Appel passé',
      RDV_PRIS: 'Rendez-vous pris',
      INFO_RGPD: 'Information RGPD envoyée',
    };
    return libelles[action] || action;
  }

  libelleTrigger(type: string): string {
    const libelles: { [cle: string]: string } = {
      RECRUTEMENT: 'Recrutement en cours',
      CREATION: 'Création récente',
      CHANGEMENT_DIRIGEANT: 'Changement de dirigeant',
      TRANSFERT_SIEGE: 'Transfert de siège',
      AUGMENTATION_CAPITAL: 'Augmentation de capital',
      MODIFICATION: 'Modification au BODACC',
    };
    return libelles[type] || type;
  }

  libelleSecteur(cle?: string): string {
    if (!cle) {
      return '—';
    }
    const secteur = this.referentiels?.secteurs.find((s) => s.cle === cle);
    return secteur?.libelle || cle;
  }

  effectif(company: ProspectCompany): string {
    if (company.effectif_min == null && company.effectif_max == null) {
      return '—';
    }
    if (company.effectif_max == null) {
      return `${company.effectif_min}+`;
    }
    return `${company.effectif_min}-${company.effectif_max}`;
  }

  motifProduit(company: ProspectCompany): string | null {
    return company.score_detail?.['_motif_produit'] || null;
  }

  eligibleRia(company: ProspectCompany): boolean {
    return !!company.score_detail?.['_eligible_ria'];
  }

  /** Composantes du score, hors clés techniques préfixées par « _ ». */
  composantesScore(company: ProspectCompany): { cle: string; valeur: number }[] {
    const detail = company.score_detail || {};
    const libelles: { [cle: string]: string } = {
      effectif: 'Effectif',
      secteur: 'Secteur prioritaire',
      recrutement: 'Recrutement en cours',
      signal_bodacc: 'Signal BODACC',
      dirigeant_identifie: 'Dirigeant identifié',
      anciennete: 'Ancienneté ≥ 2 ans',
    };
    return Object.keys(detail)
      .filter((cle) => !cle.startsWith('_'))
      .map((cle) => ({ cle: libelles[cle] || cle, valeur: detail[cle] as number }));
  }

  contactsAppelables(company: ProspectCompany): number {
    return (company.contacts || []).filter(
      (c: ProspectContactMini) => c.consentement_appel && !c.opposition
    ).length;
  }
}
